import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import SEOHead, { getBreadcrumbSchema } from '@/components/shared/SEOHead';
import CTASection from '@/components/home/CTASection';
import { projects } from '@/data/mock';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const categories = ['All', 'Transportation', 'Structural', 'Utilities', 'Water Resources', 'Land Development'];

const ProjectsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter(p => p.category === activeCategory);

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://redspire.com' },
    { name: 'Projects', url: 'https://redspire.com/projects' }
  ]);

  return (
    <Layout>
      <SEOHead
        title="Our Projects"
        description="Explore Redspire's portfolio of landmark infrastructure projects including transit corridors, bridges, utilities, and water resources engineering in Connecticut."
        keywords={['civil engineering projects', 'infrastructure development', 'Connecticut construction', 'transportation projects']}
        schema={breadcrumbSchema}
      />

      <PageHeader
        title="Our Projects"
        subtitle="Landmark infrastructure achievements shaping communities and advancing mobility."
        breadcrumb="Projects"
        backgroundImage="https://images.unsplash.com/photo-1529926691761-20fb82067c71?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      {/* Projects Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'outline'}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  'px-6 transition-all duration-200',
                  activeCategory === category
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                )}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Link key={project.id} to={project.href}>
                <Card
                  className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent" />
                    <Badge
                      className={cn(
                        'absolute top-4 left-4',
                        project.status === 'Completed'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-amber-600 hover:bg-amber-700'
                      )}
                    >
                      {project.status}
                    </Badge>
                    <Badge variant="secondary" className="absolute top-4 right-4 bg-white/90 text-gray-700">
                      {project.category}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="inline-flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all">
                        View Project Details
                        <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </Layout>
  );
};

export default ProjectsPage;
